import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Brain, GraduationCap, ShieldCheck, Sparkles, Timer } from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

/**
 * `/usp` — FuvestPro USP landing (status: waitlist)
 *
 * Enquanto os 3 Gates do FuvestPro não passarem (8+ pré-cadastros pagos,
 * parecer jurídico, viabilidade técnica) esta página captura interesse e
 * intenção de pré-pagamento. Núcleo (parser, simulador, corretores) entra
 * apenas na Fase M4 do roadmap.
 *
 * Accent: USP-blue (`var(--color-accent)`) injetado localmente via style.
 */

const uspAccent = {
  "--color-accent": "var(--color-accent)",
  "--color-accent-deep": "var(--color-accent-deep)",
  "--color-accent-soft": "var(--color-accent-soft)",
} as React.CSSProperties;

const milestones = [
  {
    icon: Brain,
    title: "Engenharia reversa em 10 anos de Fuvest",
    description:
      "Corpus de 1ª e 2ª fase classificado em taxonomia de 9 níveis: área, disciplina, tema, subtema, habilidade, tipo de questão, padrão de cobrança, risco de erro, estratégia.",
  },
  {
    icon: GraduationCap,
    title: "Foco Engenharia de Produção (Poli-USP)",
    description:
      "Pesos do curso aplicados na nota projetada. Repertório alinhado ao perfil intelectual valorizado: pensamento sistêmico, modelagem quantitativa, leitura densa.",
  },
  {
    icon: Timer,
    title: "Simulado de 1ª fase + corretor de 2ª fase",
    description:
      "90 questões na 1ª fase com cronômetro de 5 horas. Discursivas da 2ª fase corrigidas item a item com rubrica e resposta-modelo Fuvest comentada.",
  },
];

export function UspLandingPage() {
  return (
    <div style={uspAccent}>
      {/* ───── HERO ───── */}
      <section className="relative overflow-hidden bg-mesh">
        <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-30 blur-2xl animate-blob" />
        <Blob color="violet" className="pointer-events-none absolute -right-24 top-40 w-80 opacity-20 blur-2xl animate-blob" />
        <SoftStar color="mint" className="pointer-events-none absolute right-10 bottom-20 w-24 opacity-60 animate-float" />

        <Container size="wide" className="relative pt-20 pb-24 md:pt-28">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Voltar para a plataforma
          </Link>

          <div className="mt-8 max-w-3xl animate-fade-up">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                className="gap-1.5"
                style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
              >
                <Sparkles className="size-3" aria-hidden />
                Banca Fuvest
              </Badge>
              <Badge variant="outline" className="gap-1.5">
                <Timer className="size-3" aria-hidden />
                Em construção · lista de espera aberta
              </Badge>
            </div>

            <h1 className="mt-7 font-display text-[length:var(--text-display-xl)] font-semibold leading-[var(--text-display-xl--line-height)] tracking-[var(--text-display-xl--letter-spacing)] text-balance">
              <span className="text-gradient-accent">FuvestPro USP.</span> Para quem quer Engenharia de Produção na Poli.
            </h1>

            <p className="mt-6 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              Estamos construindo o FuvestPro com o mesmo rigor do PsiEinstein:
              engenharia reversa de 10 anos de prova, taxonomia detalhada, simulados
              calibrados, correção de redação e discursivas com rubrica oficial. Entre
              na lista de espera para garantir vaga no beta com desconto vitalício.
            </p>

            <div className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
              <Button asChild size="lg" variant="primary">
                <Link to="/cadastro?tenant=usp">
                  Entrar na lista de espera
                  <ArrowUpRight className="size-4" aria-hidden />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/">Conhecer a plataforma</Link>
              </Button>
            </div>

            <p className="mt-4 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Sem cobrança agora — sinalização de interesse para abertura do beta.
            </p>
          </div>
        </Container>
      </section>

      {/* ───── O que vem aí ───── */}
      <section className="border-t border-[--color-border] py-24 md:py-32">
        <Container size="wide">
          <div className="max-w-3xl">
            <Badge variant="outline" className="w-fit">
              O que estamos construindo
            </Badge>
            <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Três pilares antes do beta.
            </h2>
            <p className="mt-4 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-relaxed text-pretty">
              O FuvestPro só entra em ar depois que validarmos demanda real, parecer
              jurídico sobre uso de provas Fuvest e viabilidade técnica do pipeline.
              Quando os três gates passarem, você é avisada primeiro.
            </p>
          </div>

          <ul className="mt-14 grid gap-5 md:grid-cols-3">
            {milestones.map(({ icon: Icon, title, description }) => (
              <li
                key={title}
                className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] lift"
              >
                <span
                  className="chip size-11"
                  style={{ backgroundColor: "var(--color-accent-soft)", color: "var(--color-accent-deep)" }}
                >
                  <Icon className="size-5" aria-hidden />
                </span>
                <h3 className="mt-5 font-display text-[length:var(--text-body)] font-semibold leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                  {description}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="size-3.5" aria-hidden />
              LGPD · dados minimizados
            </span>
            <span className="inline-flex items-center gap-2">
              <Brain className="size-3.5" aria-hidden />
              Psicometria por banca
            </span>
          </div>
        </Container>
      </section>
    </div>
  );
}
